import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { ShareWithPicker } from '@/components/ShareWithPicker';

const PROJECT_TYPES = [
  'Kitchen Remodel',
  'Bathroom Remodel',
  'Roof Replacement',
  'Flooring',
  'Painting',
  'Addition / Expansion',
  'HVAC',
  'Plumbing',
  'Electrical',
  'Landscaping / Outdoor',
  'Other',
];

const STATUS_OPTIONS = [
  { value: 'planning', label: 'Planning' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const RenovationDialog = ({
  open,
  onClose,
  selectedDate,
  properties,
  selectedProperty,
  editingProject = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    property_id: selectedProperty?.id || '',
    title: '',
    project_type: 'Other',
    description: '',
    contractor_name: '',
    estimated_budget: 0,
    start_date: '',
    estimated_end_date: '',
    status: 'planning',
    progress_percent: 0,
    notes: '',
    assigned_to: '',
    collaborator_ids: [],
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (editingProject) {
      setFormData({
        property_id: editingProject.property_id || '',
        title: editingProject.title || '',
        project_type: editingProject.project_type || 'Other',
        description: editingProject.description || '',
        contractor_name: editingProject.contractor_name || '',
        estimated_budget: editingProject.estimated_budget || 0,
        start_date: editingProject.start_date || '',
        estimated_end_date: editingProject.estimated_end_date || '',
        status: editingProject.status || 'planning',
        progress_percent: editingProject.progress_percent || 0,
        notes: editingProject.notes || '',
        assigned_to: editingProject.assigned_to || '',
        collaborator_ids: editingProject.collaborator_ids || [],
      });
      if (editingProject.start_date) setStartDate(new Date(editingProject.start_date + 'T00:00:00'));
      if (editingProject.estimated_end_date) setEndDate(new Date(editingProject.estimated_end_date + 'T00:00:00'));
    } else {
      setFormData(prev => ({
        ...prev,
        property_id: selectedProperty?.id || '',
        start_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
      }));
      if (selectedDate) setStartDate(selectedDate);
    }
  }, [open, selectedProperty, selectedDate, editingProject]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.property_id || !formData.title) {
      toast.error('Please fill in property and title');
      return;
    }
    setLoading(true);
    try {
      if (editingProject) {
        const { property_id, ...payload } = formData;
        await api.put(`/renovation-projects/${editingProject.id}`, payload);
        toast.success('Renovation project updated');
      } else {
        await api.post('/renovation-projects', formData);
        toast.success('Renovation project created');
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save renovation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="renovation-dialog">
        <DialogHeader>
          <DialogTitle className="font-manrope">
            {editingProject ? 'Edit ' : 'New '}Renovation Project
          </DialogTitle>
          <DialogDescription className="text-slate-500">Track a renovation project with budget, timeline, and progress.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Property *</Label>
            <Select
              value={formData.property_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, property_id: value }))}
              disabled={!!editingProject}
            >
              <SelectTrigger data-testid="renovation-property-select">
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Master bathroom remodel"
              className="bg-slate-50"
              data-testid="renovation-title-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Project Type</Label>
              <Select
                value={formData.project_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, project_type: value }))}
              >
                <SelectTrigger data-testid="renovation-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger data-testid="renovation-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn('w-full justify-start text-left font-normal bg-slate-50', !startDate && 'text-muted-foreground')}
                    data-testid="renovation-start-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP') : 'Select'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date);
                      setFormData(prev => ({ ...prev, start_date: date ? format(date, 'yyyy-MM-dd') : '' }));
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Est. End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn('w-full justify-start text-left font-normal bg-slate-50', !endDate && 'text-muted-foreground')}
                    data-testid="renovation-end-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP') : 'Select'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      setEndDate(date);
                      setFormData(prev => ({ ...prev, estimated_end_date: date ? format(date, 'yyyy-MM-dd') : '' }));
                    }}
                    disabled={(date) => startDate && date < startDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Contractor</Label>
              <Input
                value={formData.contractor_name}
                onChange={(e) => setFormData(prev => ({ ...prev, contractor_name: e.target.value }))}
                placeholder="ABC Construction"
                className="bg-slate-50"
                data-testid="renovation-contractor-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Est. Budget ($)</Label>
              <Input
                type="number"
                min="0"
                value={formData.estimated_budget}
                onChange={(e) => setFormData(prev => ({ ...prev, estimated_budget: parseFloat(e.target.value) || 0 }))}
                className="bg-slate-50"
                data-testid="renovation-budget-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes / Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Scope, contractor info, etc."
              rows={2}
              className="bg-slate-50"
              data-testid="renovation-description-input"
            />
          </div>

          {/* Share With (collaborators) */}
          <ShareWithPicker
            value={formData.collaborator_ids}
            onChange={(ids) => setFormData((prev) => ({ ...prev, collaborator_ids: ids }))}
            excludeIds={[formData.assigned_to]}
            testIdPrefix="renovation-share-with"
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onClose()}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="renovation-submit-btn"
            >
              {loading ? 'Saving...' : editingProject ? 'Save Changes' : 'Create Project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RenovationDialog;
